/**
 * One file, from the file picker to an `upload_id` a publish can claim
 * (`KLQ-20260819-005`, "Upload, and where it can fail").
 *
 * The pipeline is four steps, and each one is a different kind of failure:
 *
 *   hash → `POST /uploads` → PUT the bytes → `POST /confirm` → poll `GET /uploads/{id}`
 *
 * **The hash comes first and is not optional.** `POST /uploads` requires `content_sha256`,
 * because it is what makes the `already_present` short-circuit possible: a file this
 * workspace already holds transfers zero bytes, which on a re-issued drawing set is the whole
 * upload. The claim is verified server-side at `confirm` against the stored object, so a
 * wrong digest is refused rather than trusted.
 *
 * **Hashing is chunked.** `crypto.subtle.digest` takes one buffer, so a 100 MB drawing means
 * reading 100 MB into memory and blocking the main thread while it is digested. Chunking
 * cannot make `subtle.digest` incremental — the Web Crypto API has no streaming digest — so
 * this reads the file in slices to keep memory flat, and yields between slices so the drawer
 * can paint progress and stay responsive. The digest itself runs once over the assembled
 * bytes; if that ever becomes the bottleneck, the answer is a worker, not a second algorithm.
 *
 * **`crypto.subtle` needs a secure context.** It is `undefined` on plain HTTP other than
 * `localhost`, which would surface as an unreadable crash at the moment somebody drops a
 * file. `hashFile` checks and throws a named error the drawer can explain instead.
 */

import {
  confirmUpload,
  readUploadState,
  requestUpload,
  type UploadKind,
  type UploadState,
} from './api';

/** Slice size for reading the file. Big enough to be few reads, small enough to stay smooth. */
const CHUNK_BYTES = 8 * 1024 * 1024;

/** How the upload can end badly, in the shapes the drawer has different sentences for. */
export type UploadFailure =
  | 'insecure_context'
  | 'hash_failed'
  | 'transfer_failed'
  | 'rejected'
  | 'timed_out';

export class UploadError extends Error {
  readonly failure: UploadFailure;
  /** The server's `failure_reason` when it had one — the format/scan detail. */
  readonly reason: string | null;

  constructor(failure: UploadFailure, reason: string | null = null) {
    super(failure);
    this.name = 'UploadError';
    this.failure = failure;
    this.reason = reason;
  }
}

export interface UploadProgress {
  /** `0`–`1` across the whole pipeline: hashing is the first third, the PUT the rest. */
  readonly fraction: number;
  readonly phase: 'hashing' | 'uploading' | 'checking';
}

export interface UploadResult {
  readonly uploadId: string;
  /** `clean`, `flagged`, or `null` when the server has not said. `flagged` still publishes. */
  readonly validationState: string | null;
  /** True when the bytes never travelled — the workspace already had them. */
  readonly deduplicated: boolean;
}

function toHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Let the browser paint between slices. */
function yieldToPaint(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * SHA-256 of a file, as 64 lowercase hex characters — the shape `_SHA256_PATTERN` demands.
 *
 * Reads in slices and reports progress; assembles into one buffer for the digest, because
 * Web Crypto has no incremental API. See the module note.
 */
export async function hashFile(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  if (typeof crypto === 'undefined' || crypto.subtle === undefined) {
    throw new UploadError('insecure_context');
  }

  const bytes = new Uint8Array(file.size);
  let offset = 0;
  try {
    while (offset < file.size) {
      const end = Math.min(offset + CHUNK_BYTES, file.size);
      const slice = new Uint8Array(await file.slice(offset, end).arrayBuffer());
      bytes.set(slice, offset);
      offset = end;
      onProgress?.(file.size === 0 ? 1 : offset / file.size);
      await yieldToPaint();
    }
    return toHex(await crypto.subtle.digest('SHA-256', bytes));
  } catch (error) {
    if (error instanceof UploadError) throw error;
    throw new UploadError('hash_failed');
  }
}

/**
 * PUT the bytes to the signed URL, echoing the headers the ticket named.
 *
 * `XMLHttpRequest` rather than `fetch`, for the one thing `fetch` still cannot do: report
 * upload progress. A drawing set is tens of megabytes on a site connection, and a publish
 * dialog with no progress reads as a frozen one.
 *
 * The headers are echoed **verbatim and in full**: `Content-Type` and `Content-Length` are
 * part of what the URL was signed for, so changing or omitting one turns a correct upload
 * into a signature mismatch.
 */
function putBytes(
  url: string,
  headers: Readonly<Record<string, string>>,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url, true);
    for (const [name, value] of Object.entries(headers)) {
      // The browser sets `Content-Length` itself and forbids setting it from script; the
      // signature covers the value it will send, which is the file's own size.
      if (name.toLowerCase() === 'content-length') continue;
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else reject(new UploadError('transfer_failed', `HTTP ${String(request.status)}`));
    };
    request.onerror = () => {
      reject(new UploadError('transfer_failed'));
    };
    request.onabort = () => {
      reject(new UploadError('transfer_failed'));
    };
    request.send(file);
  });
}

/** How long to wait for validation before giving up on it. Scanning is seconds, not minutes. */
const POLL_ATTEMPTS = 60;
const POLL_INTERVAL_MS = 1000;

/**
 * Poll until the verdict lands.
 *
 * The two state machines are separate and both matter (`files/service.py`): the *upload* goes
 * `requested → confirmed → validating → ready | failed`, and the *blob* carries the
 * validation verdict. An upload can reach `ready` with a `quarantined` blob — the bytes are
 * stored and the scanner found something that must never be published, which `attach_upload`
 * refuses server-side. Refusing it here too is not a duplicated rule; it is telling the person
 * before they type a version note that the file cannot go in.
 *
 * `flagged` is the case that must **not** be refused: active content in a PDF is a finding the
 * record keeps (`MCK-20260812-004` §4).
 */
async function waitForReady(uploadId: string, initial: UploadState): Promise<UploadState> {
  let state = initial;
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
    if (state.state === 'failed') throw new UploadError('rejected', state.failure_reason ?? null);
    if (state.validation_state === 'quarantined') throw new UploadError('rejected', 'quarantined');
    if (state.state === 'ready') return state;
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    state = await readUploadState(uploadId);
  }
  // Not a rejection: the bytes are stored and the scan may still finish. The person is told
  // it is taking unusually long rather than that their file was refused.
  throw new UploadError('timed_out');
}

/**
 * Run the whole pipeline for one file.
 *
 * Returns the `upload_id` a publish claims. `flagged` validation is **not** a failure — active
 * content in a PDF is a finding the record keeps, and `MCK-20260812-004` §4 is explicit that
 * it is reported rather than refused.
 *
 * **`idempotencyKey` is the caller's, not this function's.** `POST /uploads` demands one, and
 * minting it here would defeat the point: a retry after a failed transfer would carry a fresh
 * key and mint a second staging object, which is exactly what the header exists to prevent.
 * The key belongs to *one chosen file* — minted where the file is picked, kept for as long as
 * that file is the chosen one, and replaced when a different file takes its place. The server
 * fingerprints the body against it, so the same key with a different file is
 * `409 idempotency_key_reused`.
 */
export async function uploadFile(
  file: File,
  kind: UploadKind,
  idempotencyKey: string,
  onProgress?: (progress: UploadProgress) => void,
): Promise<UploadResult> {
  const report = (fraction: number, phase: UploadProgress['phase']): void => {
    onProgress?.({ fraction, phase });
  };

  const digest = await hashFile(file, (fraction) => {
    report(fraction * 0.3, 'hashing');
  });

  const ticket = await requestUpload(
    {
      filename: file.name,
      // Advisory only — the server sniffs magic bytes and a disagreement fails validation.
      mime_type: file.type === '' ? 'application/octet-stream' : file.type,
      byte_size: file.size,
      kind,
      content_sha256: digest,
    },
    idempotencyKey,
  );

  if (ticket.status === 'already_present') {
    report(1, 'checking');
    return {
      uploadId: ticket.upload_id,
      validationState: ticket.validation_state,
      deduplicated: true,
    };
  }

  await putBytes(ticket.upload_url, ticket.required_headers, file, (fraction) => {
    report(0.3 + fraction * 0.6, 'uploading');
  });

  report(0.9, 'checking');
  const confirmed = await confirmUpload(ticket.upload_id);
  const ready = await waitForReady(ticket.upload_id, confirmed);
  report(1, 'checking');

  return {
    uploadId: ready.upload_id,
    validationState: ready.validation_state ?? null,
    deduplicated: false,
  };
}
